/**
 * Local configuration management
 */

import { readFile, writeFile, mkdir, rename, unlink } from 'fs/promises';
import { existsSync } from 'fs';
import { homedir } from 'os';
import { join } from 'path';
import type { Config, RemoteTask, TaskStatus } from '@spacecode/shared/remote';

const CONFIG_DIR = join(homedir(), '.spacecode');

// Allow overriding config dir for testing
export function getConfigDir(): string {
  return process.env.SPACECODE_CONFIG_DIR || CONFIG_DIR;
}

/**
 * Ensure config directory exists
 */
async function ensureConfigDir(): Promise<void> {
  const dir = getConfigDir();
  if (!existsSync(dir)) {
    await mkdir(dir, { recursive: true, mode: 0o700 });
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Load configuration with basic schema validation.
 * Returns empty config if the file is missing, corrupt, or has an unexpected shape.
 */
export async function loadConfig(): Promise<Config> {
  try {
    const data = await readFile(join(getConfigDir(), 'config.json'), 'utf-8');
    const parsed = JSON.parse(data);
    if (!isPlainObject(parsed)) {
      console.warn('config.json has unexpected shape, using defaults');
      return {};
    }
    return parsed as Config;
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return {};
    console.warn(`Failed to load config.json: ${err instanceof Error ? err.message : String(err)}`);
    return {};
  }
}

/**
 * Save configuration
 */
export async function saveConfig(config: Config): Promise<void> {
  await ensureConfigDir();
  await writeFile(join(getConfigDir(), 'config.json'), JSON.stringify(config, null, 2), { mode: 0o600 });
}

export interface CloudflareCredentials {
  apiToken?: string;
  accountId?: string;
}

/**
 * Load Cloudflare credentials from ~/.spacecode/cloudflare.json
 */
export async function loadCloudflareCredentials(): Promise<CloudflareCredentials> {
  const cloudflareFile = join(getConfigDir(), 'cloudflare.json');
  try {
    const data = await readFile(cloudflareFile, 'utf-8');
    const parsed = JSON.parse(data);
    if (!isPlainObject(parsed)) {
      console.warn('cloudflare.json has unexpected shape, using defaults');
      return {};
    }
    return parsed as CloudflareCredentials;
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return {};
    console.warn(`Failed to load cloudflare.json: ${err instanceof Error ? err.message : String(err)}`);
    return {};
  }
}

/**
 * Check if Cloudflare credentials are configured
 */
export async function hasCloudflareCredentials(): Promise<boolean> {
  const creds = await loadCloudflareCredentials();
  return !!(creds.apiToken && creds.accountId);
}

const TASKS_FILE = 'tasks.json';

export async function loadTasks(): Promise<RemoteTask[]> {
  const configDir = getConfigDir();
  const tasksPath = join(configDir, TASKS_FILE);
  try {
    const data = await readFile(tasksPath, 'utf-8');
    const parsed = JSON.parse(data);
    if (!Array.isArray(parsed)) {
      console.warn('tasks.json has unexpected shape, using empty list');
      return [];
    }
    return parsed as RemoteTask[];
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return [];
    console.warn(`Failed to load tasks.json: ${err instanceof Error ? err.message : String(err)}`);
    return [];
  }
}

export async function saveTasks(tasks: RemoteTask[]): Promise<void> {
  const configDir = getConfigDir();
  await mkdir(configDir, { recursive: true, mode: 0o700 });
  const tasksPath = join(configDir, TASKS_FILE);
  // Atomic write: write to temp file then rename to prevent partial reads
  // during concurrent access from multiple exec/poll processes
  const tmpPath = `${tasksPath}.${process.pid}.tmp`;
  await writeFile(tmpPath, JSON.stringify(tasks, null, 2));
  await rename(tmpPath, tasksPath);
}

const LOCK_FILE = 'tasks.lock';
const LOCK_TIMEOUT_MS = 10_000;
const LOCK_RETRY_DELAY_MS = 50;

/**
 * Check if a lock file at the given path is stale (>30s old)
 */
async function isLockStaleImpl(lockPath: string): Promise<boolean> {
  const { stat } = await import('fs/promises');
  try {
    const lockStat = await stat(lockPath);
    // If the lock is old enough, check if the PID that holds it is still alive
    if (Date.now() - lockStat.mtimeMs > 30_000) {
      return true;
    }
    // Check if the PID that holds the lock is still alive
    try {
      const content = await readFile(lockPath, 'utf-8');
      const pid = parseInt(content, 10);
      if (!isNaN(pid)) {
        try { process.kill(pid, 0); return false; } catch (killErr) {
          // ESRCH = process doesn't exist = stale; EPERM = process exists but not ours = not stale
          return (killErr as NodeJS.ErrnoException).code === 'ESRCH';
        }
      }
    } catch {
      // Could not read lock file content, treat as potentially stale
    }
    return false;
  } catch {
    return true; // Missing lock file is effectively "stale"
  }
}

/**
 * Attempt to acquire a lock, with optional stale lock removal
 */
async function tryAcquireLockImpl(lockPath: string, forceStaleRemoval = false): Promise<boolean> {
  const { open, unlink } = await import('fs/promises');
  try {
    const fd = await open(lockPath, 'wx');
    await fd.writeFile(String(process.pid));
    await fd.close();
    return true;
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'EEXIST') {
      // Check if lock is stale
      if (forceStaleRemoval || await isLockStaleImpl(lockPath)) {
        try {
          await unlink(lockPath);
        } catch {
          // Lock already removed, retry
        }
        // Immediately retry acquiring after removing stale lock
        return await tryAcquireLockImpl(lockPath, false);
      }
      return false; // Lock is held by another process
    }
    throw err;
  }
}

/**
 * Acquire a file lock using O_CREAT|O_EXCL for atomic creation.
 * Retries with backoff until timeout. Stale locks (>30s) are forcibly removed.
 */
async function withTaskLock<T>(fn: () => Promise<T>): Promise<T> {
  const configDir = getConfigDir();
  await mkdir(configDir, { recursive: true, mode: 0o700 });
  const lockPath = join(configDir, LOCK_FILE);
  const startTime = Date.now();

  // Acquire lock with timeout
  let lockAcquired = false;
  while (Date.now() - startTime < LOCK_TIMEOUT_MS) {
    lockAcquired = await tryAcquireLockImpl(lockPath, Date.now() - startTime > LOCK_TIMEOUT_MS / 2);
    if (lockAcquired) break;
    await new Promise(resolve => setTimeout(resolve, LOCK_RETRY_DELAY_MS));
  }

  if (!lockAcquired) {
    throw new Error('Could not acquire task lock after timeout');
  }

  try {
    return await fn();
  } finally {
    await unlink(lockPath).catch((err) => {
      if ((err as NodeJS.ErrnoException).code !== 'ENOENT') {
        console.warn(`Failed to release task lock: ${err instanceof Error ? err.message : String(err)}`);
      }
    });
  }
}

export async function addTask(task: RemoteTask): Promise<void> {
  await withTaskLock(async () => {
    const tasks = await loadTasks();
    tasks.push(task);
    await saveTasks(tasks);
  });
}

export async function addTasks(newTasks: RemoteTask[]): Promise<void> {
  if (newTasks.length === 0) return;
  await withTaskLock(async () => {
    const tasks = await loadTasks();
    tasks.push(...newTasks);
    await saveTasks(tasks);
  });
}

export async function updateTask(id: string, updates: Partial<RemoteTask>): Promise<boolean> {
  return withTaskLock(async () => {
    const tasks = await loadTasks();
    const index = tasks.findIndex(t => t.id === id);
    if (index === -1) {
      return false;
    }
    tasks[index] = { ...tasks[index], ...updates };
    await saveTasks(tasks);
    return true;
  });
}

export async function getTask(id: string): Promise<RemoteTask | undefined> {
  const tasks = await loadTasks();
  return tasks.find(t => t.id === id);
}

export async function pruneTasks(olderThanDays = 7): Promise<number> {
  return withTaskLock(async () => {
    const tasks = await loadTasks();
    const cutoff = Date.now() - olderThanDays * 24 * 60 * 60 * 1000;
    const active = tasks.filter(t =>
      t.status === 'running' || t.status === 'pending' ||
      new Date(t.createdAt).getTime() > cutoff
    );
    const pruned = tasks.length - active.length;
    if (pruned > 0) {
      await saveTasks(active);
    }
    return pruned;
  });
}

/**
 * Cancel a task by ID (marks as cancelled, doesn't destroy machine)
 */
export async function cancelTask(id: string): Promise<boolean> {
  const task = await getTask(id);
  if (!task) {
    return false;
  }
  await updateTask(id, {
    status: 'cancelled' as TaskStatus,
    completedAt: new Date().toISOString(),
  });
  return true;
}

/**
 * Export withTaskLock for use in tests and other modules
 */
export { withTaskLock };

/**
 * Export lock status checking for tests
 */
export { isLockStaleImpl as isLockStale };

/**
 * JWT token and user info from OAuth authentication
 */
export interface AuthConfig {
  accessToken?: string;
  refreshToken?: string;
  user?: {
    id: string;
    githubId: string;
    githubLogin: string;
  };
  expiresAt?: string;
}

const AUTH_FILE = 'auth.json';

/**
 * Load auth configuration from ~/.spacecode/auth.json.
 * Returns empty auth config if the file is missing, corrupt, or has an unexpected shape.
 */
export async function loadAuth(): Promise<AuthConfig> {
  const authPath = join(getConfigDir(), AUTH_FILE);
  try {
    const data = await readFile(authPath, 'utf-8');
    const parsed = JSON.parse(data);
    if (!isPlainObject(parsed)) {
      console.warn('auth.json has unexpected shape, using defaults');
      return {};
    }
    return parsed as AuthConfig;
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return {};
    console.warn(`Failed to load auth.json: ${err instanceof Error ? err.message : String(err)}`);
    return {};
  }
}

/**
 * Save auth configuration to ~/.spacecode/auth.json
 */
export async function saveAuth(auth: AuthConfig): Promise<void> {
  await ensureConfigDir();
  const authPath = join(getConfigDir(), AUTH_FILE);
  await writeFile(authPath, JSON.stringify(auth, null, 2), { mode: 0o600 });
}

/**
 * Clear auth configuration (logout)
 */
export async function clearAuth(): Promise<void> {
  const authPath = join(getConfigDir(), AUTH_FILE);
  try {
    await unlink(authPath);
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code !== 'ENOENT') {
      throw new Error(`Failed to remove auth file: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
}

/**
 * Make `--api-key sk_...` act as the SpaceCode credential (same as SPACECODE_TOKEN).
 * Other values are provider (BYOK) keys and are left to the launch path. See #166.
 */
export function applyApiKeyFlag(key: string | undefined): void {
  if (key?.startsWith('sk_')) process.env.SPACECODE_TOKEN = key;
}

/**
 * Resolve the auth token for the sandbox worker.
 * Priority: env var (SPACECODE_TOKEN) -> JWT from auth.json -> refresh token.
 *
 * If SPACECODE_TOKEN is a PAT (starts with "sct_"), it's exchanged for a
 * short-lived JWT because the sandbox worker requires JWTs.
 */
export async function resolveAuthToken(): Promise<string> {
  // Env var override (CI, scripts, service-to-service)
  const envToken = process.env.SPACECODE_TOKEN;
  if (envToken) {
    if (envToken.startsWith('sct_')) {
      // Exchange PAT for short-lived JWT (sandbox worker needs JWT)
      const { BackendApiClient } = await import('./api/client.js');
      const { DEFAULT_API_URL } = await import('@spacecode/shared/remote');
      const config = await loadConfig();
      const apiUrl = process.env.SPACECODE_API_URL ?? config.apiUrl ?? DEFAULT_API_URL;
      const client = new BackendApiClient(apiUrl);
      const { token } = await client.exchangeForJwt(envToken);
      return token;
    }
    return envToken;
  }

  // JWT from OAuth login
  const auth = await loadAuth();
  if (auth.accessToken) {
    // Check if token looks expired by decoding the payload (base64url)
    try {
      const payloadPart = auth.accessToken.split('.')[1];
      if (payloadPart) {
        const payload = JSON.parse(Buffer.from(payloadPart, 'base64url').toString());
        const isExpired = payload.exp && payload.exp * 1000 < Date.now(); // exp is in seconds

        // If token is already expired and refresh failed, don't return it
        if (isExpired) {
          throw new Error(
            'Session expired. Set a fresh SPACECODE_TOKEN; run `spacecode login` for details.',
          );
        }
      }
    } catch (e) {
      // Re-throw auth errors, only swallow decode failures
      if (e instanceof Error && e.message.includes('spacecode login')) throw e;
      // Payload decode failed — use token as-is, server will reject if expired
    }
    return auth.accessToken;
  }

  throw new Error(
    'Not authenticated. Set SPACECODE_TOKEN (a `sct_` personal access token, or the sandbox shared secret) in your environment; run `spacecode login` for details.',
  );
}

/**
 * Extra options for `createSandboxClient` given the resolved token.
 *
 * The sandbox worker only honours `X-Acting-User-Id` for shared-secret callers
 * (it is ignored for JWTs), so a token that is not JWT-shaped is treated as the
 * raw shared secret and vouches for the internal identity (ADR-2: one shared
 * identity). Without this header the worker returns 401 on `POST /api/sandbox`.
 */
export function sandboxClientOptions(token: string): { actingUserId?: string } {
  return token.split('.').length === 3 ? {} : { actingUserId: 'internal' };
}
